import type { UserInfo } from 'firebase/auth';
import { createSelector } from 'reselect';
import getAuthStatus from '@bridebook/web/lib/auth/utils/get-auth-status';
import { IApplicationState } from '@bridebook/web/lib/types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { assertExists } from '@bridebook/web/lib/utils/assertExists';
import { createDeepEqualSelector } from '@bridebook/web/lib/utils/selectors';

const _getCurrentUser = (state: IApplicationState) => state.users?.user;
const _getUsers = (state: IApplicationState) => state.users;

export const selectCurrentUser = createSelector([_getCurrentUser], (user) => user);
export const selectLoadedUser = createSelector([_getCurrentUser], (user) => {
  assertExists(user);
  return user;
});
export const selectUserLocale = createSelector([_getCurrentUser], (user) => user?.l10n?.locale);
export const selectIsUserLoggedIn = createSelector([_getCurrentUser], (user) =>
  getAuthStatus({ user }),
);

export const selectCurrentUserContactPhone = createSelector(
  [selectCurrentUser],
  (user) => user?.contacts?.phone,
);

export const selectCurrentUserContactEmail = createSelector(
  [selectCurrentUser],
  (user) => user?.contacts?.email ?? '',
);

export const getCurrentUserName = createSelector([_getCurrentUser], (user) => user?.name);
export const getCurrentUserId = createSelector([_getCurrentUser], (user) => user?.id);
export const getCurrentUserDeleteAt = createSelector([_getCurrentUser], (user) => user?.deleteAt);
export const getCurrentUserBlocked = createSelector([_getCurrentUser], (user) =>
  Boolean(user?.blockedAt),
);

export const getProviders = createSelector([_getUsers], (users) => users.providers);

export const getSocialPhoto = createSelector([getProviders], (providers) => {
  const provider: UserInfo | undefined = Object.values(providers || {}).find(
    (x: UserInfo) => !!x?.photoURL,
  );

  return provider?.photoURL;
});

export const getProfilePhoto = createSelector([selectCurrentUser], (user) => {
  const userPhoto = user?.photo?.path;

  return userPhoto ? `${IMAGES_BASE_URL}/${userPhoto}` : '';
});

export const getProfilePhotoPath = createSelector([selectCurrentUser], (user) => user?.photo?.path);

export const getProfilePhotoUploadProgress = createSelector(
  [_getUsers],
  (users) => users.profilePhotoUploadProgress,
);

export const getPartnerNamesForm = createSelector(
  [_getUsers],
  (users) => users.partnerNamesForm.partners,
);

export const getUserActions = createDeepEqualSelector(selectCurrentUser, (user) =>
  // We need to check if viewer is loaded first to fallback to an empty object.
  // Otherwise, we wouldn't know if undefined was because the viewer is not loaded or if the user has no actions.
  user ? user.actions || {} : undefined,
);
